import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DairyQuery from '~/QueryHooks/DairyQuery'
import DairyService from '~/api/services/dairy'
import Layout from '~/components/Layout'
import { El as LayoutEl } from '~/components/Layout/style'
import Dialog from '~/components/Dialog'
import TableComponent from '~/components/TableComponent'
import CreateEditSegmentationFile from './CreateEditSegmentationFile'
import dayjs from 'dayjs'
import i18n from '~/components/i18n'
import { handleErrors } from '~/utils/helpers'
import notify from '~/components/Notify'

const getTableColumns = () => [
  { name: i18n.t('Title'), key: 'title' },
  { name: i18n.t('Month'), key: 'monthDisplay' },
  { name: i18n.t('File type'), key: 'fileType' },
  { name: i18n.t('Actions') }
]

const Segmentation: React.FC = () => {
  const { t } = useTranslation()

  const [drawerState, setDrawerState] = useState({ isVisible: false, fileId: null as string | null })
  const [deleteFileId, setDeleteFileId] = useState<string | null>(null)
  const [deleteIsBusy, setDeleteIsBusy] = useState(false)
  const [downloadBusy, setDownloadBusy] = useState(false)

  const { data: filesOptions, isLoading: isOptionsLoading } = DairyQuery.getSegmentationFilesOptions()
  const { data: files, isLoading: isFilesLoading, refetch } = DairyQuery.getSegmentationFiles()

  const tableRows = files?.rows?.map((file) => ({
    ...file,
    monthDisplay: dayjs(file.month).format('MMMM YYYY'),
    fileType: file.filename?.split('.').pop()?.toUpperCase() || '-'
  })) || []

  const actions = [
    {
      popupMessage: t('Edit'),
      type: 'icon',
      name: 'pencil',
      size: 'large',
      actionType: 'edit'
    },
    {
      popupMessage: t('Download'),
      type: 'icon',
      name: 'cloud download',
      size: 'large',
      actionType: 'download'
    },
    {
      popupMessage: t('Delete'),
      type: 'icon',
      name: 'trash',
      size: 'large',
      color: 'red',
      actionType: 'delete'
    }
  ]

  async function handleDownload(row) {
    if (downloadBusy) return
    setDownloadBusy(true)
    try {
      const response = await DairyService.getSegmentationFile(row.id)
      const link = document.createElement('a')
      link.href = response.data
      link.download = row.filename || row.title || 'file'
      link.click()
    } catch (error) {
      handleErrors(error)
    } finally {
      setDownloadBusy(false)
    }
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'download':
        return handleDownload(row)
      case 'edit':
        return setDrawerState({ isVisible: true, fileId: row.id })
      case 'delete':
        return setDeleteFileId(row.id)
      default:
        return
    }
  }

  async function handleDelete() {
    setDeleteIsBusy(true)
    try {
      await DairyService.deleteSegmentationFile(deleteFileId)
      notify(t('Successfully deleted segmentation file'), 'success')
      setDeleteFileId(null)
      refetch()
    } catch (error) {
      handleErrors(error)
    } finally {
      setDeleteIsBusy(false)
    }
  }

  function renderDeleteDialog() {
    return <Dialog
      type='abort'
      isDialogOpen={Boolean(deleteFileId)}
      textHeader={t('Delete file')}
      textContent={t('Are you sure you want to delete this file? This action cannot be undone.')}
      textSubmit={t('Delete')}
      textClose={t('Cancel')}
      handleClose={() => setDeleteFileId(null)}
      handleSubmit={handleDelete}
      isLoading={deleteIsBusy}
    />
  }

  return <Layout
    title={t('Übersicht Segmentierung')}
    isBusy={isOptionsLoading || isFilesLoading}
    childContainerSize={'auto'}
    fontSizeHeader={'18px'}
    actions={
      <LayoutEl.PageHeaderActionButton onClick={() => setDrawerState({ isVisible: true, fileId: null })}>
        {t('Create')}
      </LayoutEl.PageHeaderActionButton>
    }
    useSpinner
  >
    {renderDeleteDialog()}
    <TableComponent
      tableColumnHeaders={getTableColumns()}
      rows={tableRows}
      total={files?.rows?.length || 0}
      actions={actions}
      handleClickAction={handleClickAction}
      removePagination
    />
    <CreateEditSegmentationFile
      isVisible={drawerState.isVisible}
      fileId={drawerState.fileId}
      months={filesOptions?.months || []}
      fetchData={refetch}
      handleHiddenDrawer={() => setDrawerState({ isVisible: false, fileId: null })}
    />
  </Layout>
}

export default Segmentation
